<template>
  <!-- ── Not measurable: a tripwire counts crossings, it cannot time how long
       someone stood still. No dwell -> no trigger -> nothing to measure, ever.
       Deliberately NOT the same message as "not switched on": there is no control
       for the manager to flip here, and sending him to look for one wastes his
       time. docs/reference/service_response_measurement.md §2. -->
  <ChartPanel
    v-if="state === 'not_measurable'"
    class="srp-root"
    :title="$t('service.response.title')"
    icon="bi-person-raised-hand"
    height="auto"
    :accent="accent"
    muted
  >
    <p class="srp-note">
      {{ $t('service.response.zoneTripwire') }}
    </p>
    <p class="srp-note srp-note--dim">
      {{ $t('service.response.zoneAsArea') }}
    </p>
  </ChartPanel>

  <!-- ── Not enabled: a real opportunity, one click away. NEVER a zero — a zero
       reads as perfect service when the truth is that we never looked (§7). -->
  <ChartPanel
    v-else-if="state === 'not_enabled'"
    class="srp-root"
    :title="$t('service.response.title')"
    icon="bi-person-raised-hand"
    height="auto"
    :accent="accent"
    muted
  >
    <p class="srp-note">
      {{ $t('service.response.zoneNotEnabled') }}
    </p>
    <p class="srp-note srp-note--dim">
      <i18n-t keypath="service.response.zoneTurnOn" scope="global">
        <template #setting><em>{{ $t('service.response.zoneSetting') }}</em></template>
      </i18n-t>
    </p>
  </ChartPanel>

  <!-- Eligible and switched on, but no nightly figure yet. Rendering NOTHING here was
       wrong: a manager who just enabled the zone gets no confirmation it took, and the
       panel simply vanishes from a page it was on yesterday. Say what is happening
       instead — still no number, because a zero would read as "nobody went unserved". -->
  <ChartPanel
    v-else-if="state === 'pending'"
    class="srp-root"
    :title="$t('service.response.title')"
    icon="bi-person-raised-hand"
    height="auto"
    :accent="accent"
    muted
  >
    <p class="srp-note">
      {{ $t('service.response.zonePending') }}
    </p>
    <p class="srp-note srp-note--dim">
      {{ $t('service.response.zonePendingHint') }}
    </p>
  </ChartPanel>

  <ChartGrid v-else-if="state === 'measured'" class="srp-grid srp-root">
    <!-- ── The headline. One number IS the message, so a stat tile, not a chart. -->
    <ChartPanel
      :title="$t('service.response.title')"
      icon="bi-person-raised-hand"
      height="auto"
      :accent="accent"
    >
      <div class="srp-kpi">
        <span class="srp-kpi-num" :style="{ color: accent }">{{ totals.notServed }}</span>
        <span class="srp-kpi-word">{{ $t('service.response.notServed') }}</span>
        <!-- Tooltip wraps ONLY the icon: hovering the sentence itself put a help
             cursor on ordinary reading text. placement="bottom" because ChartPanel
             is overflow:hidden, so a bubble above the first line is clipped by the
             card edge. -->
        <Tooltip
          :text="basisTooltip"
          placement="bottom"
          align="start"
          :max-width="360"
        >
          <i class="bi bi-info-circle srp-info"></i>
        </Tooltip>
      </div>
      <p class="srp-kpi-label">
        {{ $t('service.response.zoneKpiLabel', { worth: totals.worthApproaching, span: timeSpan.toLowerCase() }) }}
      </p>

      <div class="srp-stats">
        <div class="srp-stat">
          <span class="srp-stat-k">{{ $t('service.response.longest') }}</span>
          <span class="srp-stat-v">{{ fmt(stats.longestSec) }}</span>
        </div>
        <div class="srp-stat">
          <span class="srp-stat-k">{{ $t('service.response.usual') }}</span>
          <span class="srp-stat-v">{{ fmt(stats.usualSec) }}</span>
        </div>
      </div>

      <p v-if="data.pattern" class="srp-pattern">
        <i class="bi bi-lightbulb"></i> {{ data.pattern }}
      </p>

      <p v-if="share" class="srp-share">
        <i class="bi bi-bar-chart-steps"></i>
        <template v-if="share.rank === 1 && share.zonesMeasured > 1">
          {{ $t('service.response.shareTop', { zone: share.zoneCount, store: share.storeCount }) }}
        </template>
        <template v-else>
          {{ $t('service.response.shareOf', { zone: share.zoneCount, store: share.storeCount, n: share.zonesMeasured }) }}
        </template>
      </p>

    </ChartPanel>

    <!-- ── When. A bucketed day x hour heatmap, and the buckets are a PRIVACY
         requirement, not a display choice: an exact time plus a zone is enough to
         pull that customer out of the CCTV recording, so no per-customer timestamp
         may leave the resolver. Aggregation to the hour breaks that link.
         (An event raster showing every individual moment was built first and
         rejected for exactly this reason — docs/.../service_response_measurement.md §9.)
         Reuses the page's own heatmap so it inherits the per-zone-type colour ramp
         instead of inventing one, at a fraction of the visitor heatmap's height. -->
    <ChartPanel
      :title="$t('service.response.whenTitle')"
      icon="bi-grid-3x3-gap"
      :height="340"
      :accent="accent"
      :empty="!heatmap"
    >
      <HeatmapDetectionZone
        v-if="heatmap"
        :heatmapChartData="heatmap"
        :timeSpan="timeSpan"
        :detectionZoneTypeName="detectionZoneTypeName"
        :seriesName="$t('service.response.seriesName')"
        compact
      />
    </ChartPanel>
  </ChartGrid>
</template>

<script>
/**
 * Zone-level service response — "how many customers lingered here and nobody served them".
 *
 * Presentation only: renders whatever `data` it is handed, so the demo store feeds it
 * a static JSON file and production can later feed it the nightly
 * `zone_service_response:*` payload with no change here.
 *
 * Rules this component enforces rather than assumes:
 *  - THREE empty states, not one. "Not measurable" (tripwire — no dwell, so no trigger
 *    is possible) is a different message from "not enabled" (one click away), and
 *    neither is ever a zero.
 *  - Only `noOneCame` is drawn. `gaveUp` is resolved and stored upstream but held back
 *    until the backfill shows how often tracker fragmentation manufactures it
 *    (docs/.../service_response_measurement.md §1).
 *  - Qualifiers live in tooltips, never as body prose. A caveat on the card must change
 *    what the manager does; anything else is hedging at someone who never asked.
 */
import { defineComponent, computed } from 'vue'
import ChartGrid from '@/components/ui/ChartGrid.vue'
import ChartPanel from '@/components/ui/ChartPanel.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import HeatmapDetectionZone from '@/components/HeatmapDetectionZone.vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatDuration } from '@/i18n/format'

export default defineComponent({
  name: 'ServiceResponseZonePanel',
  components: { ChartGrid, ChartPanel, Tooltip, HeatmapDetectionZone },
  props: {
    // null, {enabled:false} or {enabled:false, reason:'not_measurable'}
    data: { type: Object, default: null },
    timeSpan: { type: String, default: 'Last 30 days' },
    // Drives the heatmap's per-zone-type colour ramp from constants.js.
    detectionZoneTypeName: { type: String, default: 'Product Zone' },
    // The zone's own identity colour — meaning is carried by labels, not hue.
    accent: { type: String, default: 'rgb(0, 79, 110)' },
  },
  setup(props) {
    const { t } = useI18n()
    const state = computed(() => {
      const d = props.data
      if (!d) return 'pending'
      // Production sends an explicit state; the demo store sends the older
      // {enabled:false, reason} shape. Both are accepted so the two surfaces can
      // share this component without the demo needing a backend.
      if (d.state) return d.state
      if (d.enabled === false) {
        return d.reason === 'not_measurable' ? 'not_measurable' : 'not_enabled'
      }
      return 'measured'
    })

    const totals = computed(() => props.data?.totals ?? { notServed: 0, worthApproaching: 0 })
    const stats  = computed(() => props.data?.stats ?? { longestSec: 0, usualSec: 0 })
    const heatmap = computed(() => props.data?.heatmap ?? null)
    const share  = computed(() => props.data?.share ?? null)

    // Leads with the fact that makes the rule self-explanatory: the camera cannot
    // tell staff from shoppers. Everything else follows from that, so nothing else
    // is said. "The real number is higher" was cut at review — the exclusion is
    // already implied, and stating it read as editorialising on our own number.
    const basisTooltip = computed(() => t('service.response.basisTip'))

    const fmt = (s) => {
      if (currentLanguage() !== 'en') return formatDuration(s || 0)
      s = Math.round(s || 0)
      if (s < 60) return `${s}s`
      const m = Math.floor(s / 60)
      const r = s % 60
      return r ? `${m}m ${r}s` : `${m}m`
    }

    return { state, totals, stats, heatmap, share, basisTooltip, fmt }
  },
})
</script>

<style scoped>
/* Narrower floor than the page default so the two panels sit side by side on a wide
   screen and fold to one without a media query. */
.srp-grid { --chart-min: 380px; }

/* Matches SubPanel's 18px so this sits in the page at the same rhythm as the panels
   around it. Owned here rather than by the caller — an inline margin at the call site
   is one edit away from being lost. */
.srp-root { margin-top: 18px; }

.srp-kpi { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.srp-kpi-num { font-size: 2.4rem; font-weight: 700; line-height: 1; }
.srp-kpi-word { font-size: 1.05rem; color: #212529; font-weight: 600; }
.srp-kpi-sub { font-size: 0.86rem; color: #6b7280; margin: 4px 0 0; }
.srp-kpi-label { font-size: 0.78rem; color: #9aa1a9; margin: 2px 0 0; }

.srp-stats { display: flex; gap: 22px; margin-top: 16px; }
.srp-stat { display: flex; flex-direction: column; }
.srp-stat-k { font-size: 0.72rem; text-transform: uppercase; letter-spacing: .04em; color: #9aa1a9; }
.srp-stat-v { font-size: 1.05rem; font-weight: 600; color: #212529; }

.srp-pattern {
  margin: 14px 0 0; font-size: 0.86rem; color: #212529;
  background: #f8f9fa; border-radius: 8px; padding: 8px 12px;
}
.srp-pattern i { color: #b8860b; margin-right: 6px; }

.srp-share { margin: 10px 0 0; font-size: 0.86rem; color: #212529; }
.srp-share i { color: #6b7280; margin-right: 6px; }

.srp-info { color: #b9c0c7; font-size: 0.82rem; }
.srp-info:hover { color: #6b7280; }

.srp-note { font-size: 0.9rem; color: #6b7280; margin: 0; }
.srp-note--dim { font-size: 0.82rem; color: #9aa1a9; margin-top: 6px; }

</style>
